import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";

interface DateRange {
  startDate: Date | null;
  endDate: Date | null;
}

interface DatePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  onOpen?: () => void;
  onClose?: () => void;
  className?: string;
  buttonClassName?: string;
  align?: "left" | "right";
  iconOnly?: boolean;
  trigger?: React.ReactNode;
  isHidden?: boolean;
}

type QuickRange = "yesterday" | "last7" | "last30" | "last60" | "last365";

const DatePicker = ({
  value,
  onChange,
  onOpen,
  onClose,
  className = "",
  buttonClassName = "",
  align = "left",
  iconOnly = false,
  trigger,
  isHidden = false
}: DatePickerProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  // Initialize currentMonth to show end date on right calendar by default
  const getInitialMonth = () => {
    if (value.endDate) {
      const endDateMonth = new Date(value.endDate);
      endDateMonth.setMonth(endDateMonth.getMonth() - 1);
      return endDateMonth;
    }
    return new Date();
  };

  const [currentMonth, setCurrentMonth] = useState(getInitialMonth);
  const [selectingStart, setSelectingStart] = useState(true);
  const [tempRange, setTempRange] = useState<DateRange>(value);
  const [startDateInput, setStartDateInput] = useState("");
  const [endDateInput, setEndDateInput] = useState("");
  const pickerRef = useRef<HTMLDivElement>(null);

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        if (onClose) onClose();
        // Swallow the subsequent click event so parent handlers
        // (e.g. title button opening modals, board background opening AI banner)
        // don't fire on the same interaction that dismissed the picker.
        const swallowClick = (e: MouseEvent) => {
          e.stopPropagation();
        };
        document.addEventListener("click", swallowClick, { capture: true, once: true });
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleToggle = () => {
    const nextState = !isOpen;
    setIsOpen(nextState);
    if (nextState && onOpen) onOpen();
    if (!nextState && onClose) onClose();
  };

  // Navigate to end date when opening if range exists
  useEffect(() => {
    if (isOpen) {
      setTempRange(value);
      setStartDateInput(
        value.startDate ? formatDateInput(value.startDate) : ""
      );
      setEndDateInput(value.endDate ? formatDateInput(value.endDate) : "");

      if (value.endDate) {
        // Set currentMonth to one month before endDate so endDate appears on the right calendar
        const endDateMonth = new Date(value.endDate);
        endDateMonth.setMonth(endDateMonth.getMonth() - 1);
        setCurrentMonth(endDateMonth);
      } else if (!value.startDate && !value.endDate) {
        setCurrentMonth(new Date());
      }
    }
  }, [isOpen]);

  if (isHidden) return null;

  const handleDateClick = (date: Date) => {
    if (selectingStart) {
      const newRange = { startDate: date, endDate: null };
      setTempRange(newRange);
      setStartDateInput(formatDateInput(date));
      setEndDateInput("");
      setSelectingStart(false);
    } else {
      if (tempRange.startDate && date < tempRange.startDate) {
        // If end date is before start date, swap them
        const newRange = { startDate: date, endDate: tempRange.startDate };
        setTempRange(newRange);
        setStartDateInput(formatDateInput(date));
        setEndDateInput(formatDateInput(tempRange.startDate));
      } else {
        const newRange = { ...tempRange, endDate: date };
        setTempRange(newRange);
        setEndDateInput(formatDateInput(date));
      }
      setSelectingStart(true);
    }
  };

  const handleQuickRange = (range: QuickRange) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    let startDate: Date;
    let endDate: Date;

    switch (range) {
      case "yesterday":
        startDate = yesterday;
        endDate = yesterday;
        break;
      case "last7":
        endDate = yesterday;
        startDate = new Date(yesterday);
        startDate.setDate(startDate.getDate() - 6);
        break;
      case "last30":
        endDate = yesterday;
        startDate = new Date(yesterday);
        startDate.setDate(startDate.getDate() - 29);
        break;
      case "last60":
        endDate = yesterday;
        startDate = new Date(yesterday);
        startDate.setDate(startDate.getDate() - 59);
        break;
      case "last365":
        endDate = yesterday;
        startDate = new Date(yesterday);
        startDate.setDate(startDate.getDate() - 364);
        break;
    }

    setTempRange({ startDate, endDate });
    setStartDateInput(formatDateInput(startDate));
    setEndDateInput(formatDateInput(endDate));
    // Set currentMonth to one month before endDate so endDate appears on the right calendar
    const endDateMonth = new Date(endDate);
    endDateMonth.setMonth(endDateMonth.getMonth() - 1);
    setCurrentMonth(endDateMonth);
    setSelectingStart(true);
  };

  const isDateDisabled = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date >= today;
  };

  const isDateInRange = (date: Date) => {
    if (!tempRange.startDate) return false;
    if (!tempRange.endDate) return isSameDay(date, tempRange.startDate);
    return date >= tempRange.startDate && date <= tempRange.endDate;
  };

  const isDateRangeStart = (date: Date) => {
    return tempRange.startDate ? isSameDay(date, tempRange.startDate) : false;
  };

  const isDateRangeEnd = (date: Date) => {
    return tempRange.endDate ? isSameDay(date, tempRange.endDate) : false;
  };

  const isSameDay = (date1: Date, date2: Date) => {
    return (
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    );
  };

  const formatDate = (date: Date | null) => {
    if (!date) return "";
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatDateInput = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const parseDateInput = (input: string): Date | null => {
    if (!input) return null;
    const date = new Date(input);
    if (isNaN(date.getTime())) return null;
    return date;
  };

  const formatDisplayValue = () => {
    if (!value.startDate && !value.endDate)
      return t("datePicker.selectDateRange");
    if (value.startDate && !value.endDate)
      return `${formatDate(value.startDate)} - ...`;
    if (value.startDate && value.endDate) {
      if (isSameDay(value.startDate, value.endDate)) {
        return formatDate(value.startDate);
      }
      return `${formatDate(value.startDate)} - ${formatDate(value.endDate)}`;
    }
    return t("datePicker.selectDateRange");
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStartDateInput(e.target.value);
    const date = parseDateInput(e.target.value);
    if (date && !isDateDisabled(date)) {
      setTempRange({ ...tempRange, startDate: date });
      setCurrentMonth(date);
    }
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndDateInput(e.target.value);
    const date = parseDateInput(e.target.value);
    if (date && !isDateDisabled(date)) {
      setTempRange({ ...tempRange, endDate: date });
      setCurrentMonth(date);
    }
  };

  const handleApply = () => {
    onChange(tempRange);
    setIsOpen(false);
    if (onClose) onClose();
  };

  const handleCancel = () => {
    setTempRange(value);
    setStartDateInput(value.startDate ? formatDateInput(value.startDate) : "");
    setEndDateInput(value.endDate ? formatDateInput(value.endDate) : "");
    setIsOpen(false);
    if (onClose) onClose();
  };

  const isQuickRangeActive = (range: QuickRange): boolean => {
    if (!tempRange.startDate || !tempRange.endDate) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    let expectedStart: Date;
    let expectedEnd: Date;

    switch (range) {
      case "yesterday":
        expectedStart = yesterday;
        expectedEnd = yesterday;
        break;
      case "last7":
        expectedEnd = yesterday;
        expectedStart = new Date(yesterday);
        expectedStart.setDate(expectedStart.getDate() - 6);
        break;
      case "last30":
        expectedEnd = yesterday;
        expectedStart = new Date(yesterday);
        expectedStart.setDate(expectedStart.getDate() - 29);
        break;
      case "last60":
        expectedEnd = yesterday;
        expectedStart = new Date(yesterday);
        expectedStart.setDate(expectedStart.getDate() - 59);
        break;
      case "last365":
        expectedEnd = yesterday;
        expectedStart = new Date(yesterday);
        expectedStart.setDate(expectedStart.getDate() - 364);
        break;
    }

    return (
      isSameDay(tempRange.startDate, expectedStart) &&
      isSameDay(tempRange.endDate, expectedEnd)
    );
  };

  const renderMonth = (monthOffset: number) => {
    const date = new Date(currentMonth);
    date.setMonth(date.getMonth() + monthOffset);

    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startingDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();

    const days = [];

    // Previous month days
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="h-8" />);
    }

    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      const currentDate = new Date(year, month, day);
      const disabled = isDateDisabled(currentDate);
      const inRange = isDateInRange(currentDate);
      const isStart = isDateRangeStart(currentDate);
      const isEnd = isDateRangeEnd(currentDate);

      days.push(
        <button
          key={day}
          onClick={() => !disabled && handleDateClick(currentDate)}
          disabled={disabled}
          className={`
            h-8 w-8 text-sm rounded-md transition-colors
            ${disabled ? "text-neutral-300 dark:text-neutral-700 cursor-not-allowed" : "cursor-pointer"}
            ${!disabled && (isStart || isEnd)
              ? "bg-accent-primary text-white hover:bg-accent-primary/90"
              : !disabled && inRange
                ? "bg-accent-primary/10 dark:bg-accent-primary/20 text-text-primary dark:text-neutral-100 hover:bg-accent-primary/20 dark:hover:bg-accent-primary/30"
                : !disabled
                  ? "text-text-primary dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                  : ""
            }
          `}
        >
          {day}
        </button>
      );
    }

    return (
      <div className="flex-1 min-w-0">
        <div className="grid grid-cols-7 gap-1 mb-2">
          {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
            <div
              key={day}
              className="h-8 flex items-center justify-center text-xs text-neutral-500 dark:text-neutral-400 font-medium"
            >
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">{days}</div>
      </div>
    );
  };

  const handlePreviousMonth = () => {
    setCurrentMonth((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() - 1);
      return newDate;
    });
  };

  const handleNextMonth = () => {
    setCurrentMonth((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + 1);
      return newDate;
    });
  };

  return (
    <div className={`relative ${className}`} ref={pickerRef}>
      {trigger ? (
        <div 
          onClick={(e) => {
            e.stopPropagation();
            handleToggle();
          }} 
          className="cursor-pointer"
        >
          {trigger}
        </div>
      ) : (
        <button
          onClick={handleToggle}
          className={`${iconOnly
            ? "flex items-center justify-center p-2 bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-800 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
            : "flex items-center space-x-2 px-4 py-2 bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-800 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors text-sm max-w-full"
            } ${buttonClassName}`}
          title={iconOnly ? formatDisplayValue() : undefined}
        >
          <span className="w-4 h-4 text-neutral-500 dark:text-neutral-400 flex-shrink-0" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_ece8a8f6.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_ece8a8f6.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
          {!iconOnly && (
            <>
              <span className="text-text-primary dark:text-neutral-100 text-xs sm:text-sm whitespace-nowrap overflow-hidden text-ellipsis flex-shrink min-w-0">
                {formatDisplayValue()}
              </span>
              <span className={`w-4 h-4 text-neutral-500 dark:text-neutral-400 transition-transform flex-shrink-0 ${isOpen ? "rotate-180" : ""}`} style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_03158bb5.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_03158bb5.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
            </>
          )}
        </button>
      )}

      {isOpen && (
        <div onClick={(e) => e.stopPropagation()} className={`absolute top-full mt-2 z-50 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl shadow-lg animate-fade-in w-[90vw] sm:w-auto min-w-[300px] lg:min-w-[700px] max-w-[95vw] font-normal text-text-primary dark:text-neutral-100 ${align === "right" ? "right-0" : "left-0 -translate-x-1/4 sm:translate-x-0"}`}>
          {/* Date Inputs Header */}
          <div className="px-4 pt-4 pb-3 border-b border-neutral-200 dark:border-neutral-800">
            <div className="flex items-center gap-2 lg:gap-3">
              <div className="flex-1 min-w-0">
                <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                  {t("datePicker.startDate")}
                </label>
                <input
                  type="date"
                  value={startDateInput}
                  onChange={handleStartDateChange}
                  max={formatDateInput(
                    new Date(new Date().setDate(new Date().getDate() - 1))
                  )}
                  className="w-full px-2 lg:px-3 py-2 text-sm border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-text-primary dark:text-neutral-100 rounded-md focus:outline-none focus:ring-2 focus:ring-accent-primary [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:w-0 [&::-webkit-calendar-picker-indicator]:h-0"
                />
              </div>
              <div className="text-neutral-400 dark:text-neutral-500 pt-5">→</div>
              <div className="flex-1 min-w-0">
                <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                  {t("datePicker.endDate")}
                </label>
                <input
                  type="date"
                  value={endDateInput}
                  onChange={handleEndDateChange}
                  max={formatDateInput(
                    new Date(new Date().setDate(new Date().getDate() - 1))
                  )}
                  className="w-full px-2 lg:px-3 py-2 text-sm border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-text-primary dark:text-neutral-100 rounded-md focus:outline-none focus:ring-2 focus:ring-accent-primary [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:w-0 [&::-webkit-calendar-picker-indicator]:h-0"
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row">
            {/* Quick Ranges */}
            <div className="flex lg:flex-col gap-1 lg:space-y-1 px-4 py-3 lg:pr-4 border-b lg:border-b-0 lg:border-r border-neutral-200 dark:border-neutral-800 min-w-0 lg:min-w-[140px] overflow-x-auto lg:overflow-x-visible">
              <div className="hidden sm:block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-2">
                {t("datePicker.quickRanges")}
              </div>
              <button
                onClick={() => handleQuickRange("yesterday")}
                className={`text-left px-3 py-2 text-xs sm:text-sm rounded-md transition-colors whitespace-nowrap ${isQuickRangeActive("yesterday")
                  ? "bg-accent-primary/10 dark:bg-accent-primary/20 text-text-primary dark:text-neutral-100"
                  : "hover:bg-neutral-100 dark:hover:bg-neutral-800 text-text-primary dark:text-neutral-300"
                  }`}
              >
                {t("datePicker.yesterday")}
              </button>
              <button
                onClick={() => handleQuickRange("last7")}
                className={`text-left px-3 py-2 text-xs sm:text-sm rounded-md transition-colors whitespace-nowrap ${isQuickRangeActive("last7")
                  ? "bg-accent-primary/10 dark:bg-accent-primary/20 text-text-primary dark:text-neutral-100"
                  : "hover:bg-neutral-100 dark:hover:bg-neutral-800 text-text-primary dark:text-neutral-300"
                  }`}
              >
                {t("datePicker.last7Days")}
              </button>
              <button
                onClick={() => handleQuickRange("last30")}
                className={`text-left px-3 py-2 text-xs sm:text-sm rounded-md transition-colors whitespace-nowrap ${isQuickRangeActive("last30")
                  ? "bg-accent-primary/10 dark:bg-accent-primary/20 text-text-primary dark:text-neutral-100"
                  : "hover:bg-neutral-100 dark:hover:bg-neutral-800 text-text-primary dark:text-neutral-300"
                  }`}
              >
                {t("datePicker.last30Days")}
              </button>
              <button
                onClick={() => handleQuickRange("last60")}
                className={`text-left px-3 py-2 text-xs sm:text-sm rounded-md transition-colors whitespace-nowrap ${isQuickRangeActive("last60")
                  ? "bg-accent-primary/10 dark:bg-accent-primary/20 text-text-primary dark:text-neutral-100"
                  : "hover:bg-neutral-100 dark:hover:bg-neutral-800 text-text-primary dark:text-neutral-300"
                  }`}
              >
                {t("datePicker.last60Days")}
              </button>
              <button
                onClick={() => handleQuickRange("last365")}
                className={`text-left px-3 py-2 text-xs sm:text-sm rounded-md transition-colors whitespace-nowrap ${isQuickRangeActive("last365")
                  ? "bg-accent-primary/10 dark:bg-accent-primary/20 text-text-primary dark:text-neutral-100"
                  : "hover:bg-neutral-100 dark:hover:bg-neutral-800 text-text-primary dark:text-neutral-300"
                  }`}
              >
                {t("datePicker.last365Days")}
              </button>
            </div>

            {/* Calendar */}
            <div className="flex-1 p-4 min-w-0">
              {/* Two Months with Navigation */}
              <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
                {/* First Month */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-3">
                    <button
                      onClick={handlePreviousMonth}
                      className="p-1.5 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                    >
                      <span className="w-4 h-4 text-neutral-600 dark:text-neutral-400" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_df8554ce.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_df8554ce.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                    </button>
                    <div className="text-sm font-medium text-text-primary dark:text-neutral-100">
                      {new Date(
                        currentMonth.getFullYear(),
                        currentMonth.getMonth(),
                        1
                      ).toLocaleDateString("en-US", {
                        month: "long",
                        year: "numeric",
                      })}
                    </div>
                    {/* Next button for mobile/tablet only */}
                    <button
                      onClick={handleNextMonth}
                      className="lg:hidden p-1.5 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                    >
                      <span className="w-4 h-4 text-neutral-600 dark:text-neutral-400" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_58e5b5df.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_58e5b5df.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                    </button>
                    {/* Spacer for desktop */}
                    <div className="hidden lg:block w-8"></div>
                  </div>
                  {renderMonth(0)}
                </div>

                {/* Second Month */}
                <div className="hidden lg:block flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-8"></div>
                    <div className="text-sm font-medium text-text-primary dark:text-neutral-100">
                      {new Date(
                        currentMonth.getFullYear(),
                        currentMonth.getMonth() + 1,
                        1
                      ).toLocaleDateString("en-US", {
                        month: "long",
                        year: "numeric",
                      })}
                    </div>
                    <button
                      onClick={handleNextMonth}
                      className="p-1.5 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                    >
                      <span className="w-4 h-4 text-neutral-600 dark:text-neutral-400" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_58e5b5df.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_58e5b5df.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                    </button>
                  </div>
                  {renderMonth(1)}
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="px-4 py-3 border-t border-neutral-200 dark:border-neutral-800 flex justify-end gap-2">
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-sm font-medium text-text-primary dark:text-neutral-300 bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-800 rounded-md hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
            >
              {t("datePicker.cancel")}
            </button>
            <button
              onClick={handleApply}
              className="px-4 py-2 text-sm font-medium text-white bg-accent-primary rounded-md hover:bg-accent-primary/90 dark:hover:bg-accent-primary/80 transition-colors"
            >
              {t("datePicker.apply")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatePicker;
